'use client';

import { Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import Cookies from 'js-cookie';
import { sendGTMEvent } from '@next/third-parties/google';
import { usePostHog } from 'posthog-js/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { LuCircleChevronRight } from 'react-icons/lu';
import {
  asLinkAttrs,
  isFilled,
  type ImageField,
  type LinkField,
  type RichTextField,
} from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AnimatedInput } from '@/components/AnimatedInput';
import { getSubmissionContext } from '@/utils/attribution';
import { logger } from '@/utils/logger';
import { usePathname } from 'next/navigation';
import { POSTHOG_EVENTS, isInternalTraffic } from '@/lib/posthog';

/** Set once shown — keeps the popup to one appearance per browser session. */
const SESSION_KEY = 'popup-seen';
/** Set on dismiss (7d) or on CTA click (a year — they registered). */
const DISMISS_COOKIE = 'popup-dismissed';
const DISMISS_DAYS = 7;
const REGISTERED_DAYS = 365;

// Hard-coded per the design spec — never changes with content.
const PSGLOBAL_LOGO = '/psglobal-consulting-logo-dark-bg.png';

// Storage throws in Safari private mode / with site data blocked; a failure just
// means the popup behaves as unseen.
const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeSession = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};

const UTM_PARAMS = { utm_source: 'psghk', utm_medium: 'popup' } as const;

/**
 * Tags the CTA destination for attribution.
 *
 * Only absolute http(s) URLs are tagged — UTMs on an internal link restart the
 * analytics session and surface as a self-referral. Params already present in
 * the authored URL win, so an editor can override them per campaign.
 */
const withUtm = (href: string): string => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href; // Relative or malformed — leave it alone.
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return href;
  for (const [key, value] of Object.entries(UTM_PARAMS)) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
};

// Split-flap (Solari) board. Each digit is a 32x46 card split at its midline into
// four 23px layers: two static halves plus two folds that only exist mid-flip.
// The folds use animation-fill-mode: forwards, so their end state is pixel-identical
// to the idle state — no animationend handler is needed to "commit" the new value.
const SPLIT_FLAP_CSS = `
.psf-digit{position:relative;width:32px;height:46px;perspective:260px}
.psf-digit>div{position:absolute;left:0;width:100%;height:23px;overflow:hidden}
.psf-digit i{position:absolute;left:0;width:100%;height:46px;line-height:46px;
  font-style:normal;font-weight:600;font-size:26px;text-align:center;color:#F5F7F7;
  font-variant-numeric:tabular-nums}
.psf-t,.psf-ft{top:0;border-radius:5px 5px 0 0;background:linear-gradient(#2A2F31,#1F2426)}
.psf-t i,.psf-ft i{top:0}
.psf-b,.psf-fb{top:23px;border-radius:0 0 5px 5px;background:linear-gradient(#191D1F,#232829)}
.psf-b i,.psf-fb i{top:-23px}
.psf-t::after{content:'';position:absolute;left:0;right:0;bottom:0;height:1px;background:rgba(0,0,0,.55)}
.psf-ft{z-index:2;transform-origin:center bottom;animation:psfTop .28s ease-in forwards}
.psf-fb{z-index:2;transform-origin:center top;transform:rotateX(90deg);
  animation:psfBottom .28s ease-out .28s forwards}
@keyframes psfTop{to{transform:rotateX(-90deg)}}
@keyframes psfBottom{to{transform:rotateX(0deg)}}
.psf-colon{height:46px;line-height:46px;padding:0 5px;font-size:15px;font-weight:600;color:#5C6F6D}
.psf-lbl{margin-top:7px;font-size:9px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:#5C6F6D}
@media (prefers-reduced-motion: reduce){
  .psf-ft{display:none}
  .psf-fb{animation:none;transform:rotateX(0deg)}
}
`;

const pad = (n: number) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

// A ticking clock is external mutable state, so it is read through
// useSyncExternalStore rather than setState-in-an-effect. The snapshot is a
// primitive (whole seconds) so React can compare it cheaply.
const subscribeSecond = (onChange: () => void) => {
  const id = window.setInterval(onChange, 1000);
  return () => window.clearInterval(id);
};
const getSecondSnapshot = () => Math.floor(Date.now() / 1000);
const getServerSecondSnapshot = () => 0;

const EYEBROW_CLASS =
  'text-[11px] font-semibold uppercase leading-none tracking-[0.16em] text-[#B08A2E]';
const HEADING_CLASS =
  'mt-2.5 text-[22px] font-bold leading-[1.22] tracking-[-0.01em] text-[#0E3B39] sm:text-[28px]';
const SECONDARY_LINK_CLASS =
  'shrink-0 px-[7px] text-[13px] text-[#5C6F6D] underline-offset-4 transition-colors hover:text-[#0E3B39] hover:underline';

/** Every panel occupies the same grid cell; only opacity distinguishes them. */
const panelClass = (active: boolean) =>
  `col-start-1 row-start-1 transition-opacity duration-300 motion-reduce:transition-none ${
    active ? 'opacity-100' : 'pointer-events-none opacity-0'
  }`;

const signupSchema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().min(1, 'Required').email('Enter a valid email'),
  hp: z.string().optional(),
});
type SignupValues = z.infer<typeof signupSchema>;

/** Seconds the popup had been on screen when the visitor acted, to one decimal. */
const secondsSince = (startMs: number) =>
  startMs ? Math.round((Date.now() - startMs) / 100) / 10 : 0;

const newEventId = () => Date.now().toString();

/** One split-flap digit. Renders the two folds only while the char is changing. */
function Flap({ from, to }: { from: string; to: string }) {
  const flipping = from !== to;
  return (
    <div className="psf-digit">
      <div className="psf-t">
        <i>{to}</i>
      </div>
      <div className="psf-b">
        <i>{flipping ? from : to}</i>
      </div>
      {flipping && (
        // Keyed on `to` so a digit that changes every tick (the seconds column)
        // remounts and replays the animation instead of sitting on a finished one.
        <Fragment key={to}>
          <div className="psf-ft">
            <i>{from}</i>
          </div>
          <div className="psf-fb">
            <i>{to}</i>
          </div>
        </Fragment>
      )}
    </div>
  );
}

const unitsAt = (targetMs: number, nowSec: number) => {
  const total = Math.max(0, Math.floor(targetMs / 1000) - nowSec);
  return [
    [pad(total / 86400), 'Days'],
    [pad((total % 86400) / 3600), 'Hours'],
    [pad((total % 3600) / 60), 'Mins'],
    [pad(total % 60), 'Secs'],
  ] as const;
};

function Countdown({ targetMs }: { targetMs: number }) {
  const nowSec = useSyncExternalStore(subscribeSecond, getSecondSnapshot, getServerSecondSnapshot);
  // The previous second is derived, not remembered — the clock is the only state,
  // so there is no ref or effect to keep in sync with it.
  const current = unitsAt(targetMs, nowSec);
  const previous = unitsAt(targetMs, nowSec - 1);

  return (
    // Decorative: a per-second live region would be hostile to screen readers, and
    // the actual date and time are already stated in the rail.
    <div aria-hidden className="mt-8 hidden items-start sm:flex">
      {current.map(([value, label], unit) => (
        <Fragment key={label}>
          {unit > 0 && <span className="psf-colon">:</span>}
          <div className="flex flex-col items-center">
            <div className="flex gap-[3px]">
              {value.split('').map((char, i) => (
                <Flap key={i} from={previous[unit][0][i]} to={char} />
              ))}
            </div>
            <span className="psf-lbl">{label}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

const TITLE_COMPONENTS = {
  heading2: ({ children }: { children: React.ReactNode }) => (
    <span className="block">{children}</span>
  ),
  paragraph: ({ children }: { children: React.ReactNode }) => (
    <span className="block">{children}</span>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-bold text-[#1B9A95]">{children}</strong>
  ),
  em: ({ children }: { children: React.ReactNode }) => <em className="italic">{children}</em>,
};

// Teal 7px square markers, drawn as an absolutely positioned span per row.
const BulletRow = ({ children }: { children: React.ReactNode }) => (
  <li className="relative pl-[18px] text-[13px] font-normal leading-[1.55] text-[#5C6F6D]">
    <span
      aria-hidden
      className="absolute left-0 top-[7px] h-[7px] w-[7px] rounded-[2px] bg-[#1B9A95]"
    />
    {children}
  </li>
);

const BULLET_COMPONENTS = {
  list: ({ children }: { children: React.ReactNode }) => (
    <ul className="mt-3 space-y-[7px]">{children}</ul>
  ),
  oList: ({ children }: { children: React.ReactNode }) => (
    <ul className="mt-3 space-y-[7px]">{children}</ul>
  ),
  listItem: BulletRow,
  oListItem: BulletRow,
  paragraph: ({ children }: { children: React.ReactNode }) => (
    <p className="mt-3 text-[13px] font-normal leading-[1.55] text-[#5C6F6D]">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-semibold text-[#0E3B39]">{children}</strong>
  ),
  hyperlink: ({
    children,
    node,
  }: {
    children: React.ReactNode;
    node: { data: { url?: string; target?: string } };
  }) => (
    <a
      href={node.data.url ?? '#'}
      target={node.data.target}
      rel={node.data.target === '_blank' ? 'noopener noreferrer' : undefined}
      className="font-medium text-[#1B9A95] underline underline-offset-2"
    >
      {children}
    </a>
  ),
};

const TIME_COMPONENTS = {
  paragraph: ({ children }: { children: React.ReactNode }) => (
    <p className="text-[13px] leading-[1.5] text-white">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
};

export interface PopupModalProps {
  /** Prismic document id + locale + plain-text title, so every event can be
   *  grouped by which popup produced it. */
  popupId: string;
  lang: string;
  titleText: string;
  eyebrow: string | null;
  eyebrowMobile: string | null;
  title: RichTextField;
  bullets: RichTextField;
  image: ImageField;
  button: LinkField;
  dismissLabel: string | null;
  formEyebrow: string | null;
  formTitle: string | null;
  formNameLabel: string | null;
  formEmailLabel: string | null;
  formSubmitLabel: string | null;
  successEyebrow: string | null;
  formSuccess: string | null;
  /** Internal, English. Retitles the lead email subject + Slack header. */
  leadLabel: string | null;
  dateWeekday: string | null;
  dateDisplay: string | null;
  dateYear: string | null;
  dateLine: string | null;
  eventTimes: RichTextField;
  eventNote: string | null;
  /** ISO timestamp the countdown runs down to. */
  countdownTarget: string | null;
  /** ISO timestamp after which the popup never shows again. */
  expiresAt: string | null;
  /** Seconds to wait after mount before opening. */
  delaySeconds: number;
}

export default function PopupModal({
  popupId,
  lang,
  titleText,
  eyebrow,
  eyebrowMobile,
  title,
  bullets,
  image,
  button,
  dismissLabel,
  formEyebrow,
  formTitle,
  formNameLabel,
  formEmailLabel,
  formSubmitLabel,
  successEyebrow,
  formSuccess,
  leadLabel,
  dateWeekday,
  dateDisplay,
  dateYear,
  dateLine,
  eventTimes,
  eventNote,
  countdownTarget,
  expiresAt,
  delaySeconds,
}: PopupModalProps) {
  const [open, setOpen] = useState(false);
  // offer → the pitch; form → the two-field signup; done → confirmation.
  const [step, setStep] = useState<'offer' | 'form' | 'done'>('offer');
  const posthog = usePostHog();
  const pathname = usePathname();

  // Identifies one impression, so popup_shown / popup_cta_click / popup_dismissed
  // can be joined into a funnel rather than guessed at by person.
  const instanceRef = useRef('');
  const shownAtRef = useRef(0);
  // Radix's onOpenChange can't say *how* the dialog closed, so each dismissal
  // affordance records itself here first.
  const dismissMethodRef = useRef<'close_button' | 'no_thanks' | 'escape' | 'overlay' | 'unknown'>(
    'unknown'
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupValues>({ resolver: zodResolver(signupSchema) });

  const { href, target, rel } = asLinkAttrs(button);
  const ctaHref = href ? withUtm(href) : null;
  const ctaLabel = button.text?.trim();
  const hasCta = Boolean(ctaHref && ctaLabel);
  const targetMs = countdownTarget ? Date.parse(countdownTarget) : NaN;
  const hasCountdown = Number.isFinite(targetMs);

  const track = useCallback(
    (event: string, properties: Record<string, string | number | boolean> = {}) => {
      // Staff traffic would otherwise inflate the very rates this exists to measure.
      if (isInternalTraffic()) return;
      const payload = {
        ...properties,
        popup_id: popupId,
        popup_lang: lang,
        popup_title: titleText,
        popup_instance: instanceRef.current,
        page_path: pathname ?? '',
        timestamp: new Date().toISOString(),
      };
      posthog?.capture(event, payload);
      sendGTMEvent({ event, ...payload });
    },
    [posthog, popupId, lang, titleText, pathname]
  );

  // Open on the delay, unless already seen this session, dismissed, or expired.
  // The expiry check lives here rather than in render so a cached server render
  // can never keep a dead popup alive, and so dates never affect hydration.
  useEffect(() => {
    if (expiresAt && Date.now() > Date.parse(expiresAt)) return;
    if (readSession(SESSION_KEY) || Cookies.get(DISMISS_COOKIE)) return;

    const timer = window.setTimeout(() => {
      // Re-check on fire, not just on schedule: the flags can be set during the
      // delay window (another mount, or another tab), and "once per session"
      // should hold at the moment we actually show it.
      if (readSession(SESSION_KEY) || Cookies.get(DISMISS_COOKIE)) return;
      writeSession(SESSION_KEY, '1');
      instanceRef.current =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${performance.now()}`;
      shownAtRef.current = Date.now();
      setStep('offer');
      setOpen(true);
      track(POSTHOG_EVENTS.POPUP_SHOWN, { delay_seconds: delaySeconds });
    }, delaySeconds * 1000);

    return () => window.clearTimeout(timer);
  }, [delaySeconds, expiresAt, track]);

  const suppress = useCallback((days: number) => {
    try {
      Cookies.set(DISMISS_COOKIE, '1', { expires: days });
    } catch {
      /* ignore */
    }
  }, []);

  // Radix calls this for Escape, the overlay and the ✕ — never for our own
  // setOpen(false), so the CTA path doesn't also log a dismissal.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      suppress(DISMISS_DAYS);
      track(POSTHOG_EVENTS.POPUP_DISMISSED, {
        dismiss_method: dismissMethodRef.current,
        seconds_visible: secondsSince(shownAtRef.current),
      });
      dismissMethodRef.current = 'unknown';
    }
  };

  // The panels are all mounted (for constant height), so opening the form has to
  // move focus deliberately — otherwise focus stays on a now-inert button.
  const formPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (step !== 'form') return;
    formPanelRef.current?.querySelector<HTMLInputElement>('input:not([tabindex="-1"])')?.focus();
  }, [step]);

  // Step 1 CTA: opens the form rather than navigating. Still the intent signal.
  const handleCtaClick = () => {
    track(POSTHOG_EVENTS.POPUP_CTA_CLICK, {
      cta_label: ctaLabel ?? '',
      seconds_visible: secondsSince(shownAtRef.current),
    });
    setStep('form');
  };

  // The secondary link now navigates to the event page — it is not a dismissal,
  // so it must not be logged as one.
  const handleMoreInfoClick = () => {
    track(POSTHOG_EVENTS.POPUP_MORE_INFO_CLICK, {
      cta_href: ctaHref ?? '',
      seconds_visible: secondsSince(shownAtRef.current),
    });
  };

  const onSubmit = (values: SignupValues) => {
    // They committed — don't nag again for a year.
    suppress(REGISTERED_DAYS);
    const context = getSubmissionContext();
    const campaign = leadLabel?.trim() || titleText || 'Webinar';

    track(POSTHOG_EVENTS.POPUP_FORM_SUBMIT, {
      lead_label: campaign,
      seconds_visible: secondsSince(shownAtRef.current),
    });
    sendGTMEvent({
      event: 'generate_lead',
      category: 'Form',
      action: 'Submit',
      label: `Popup — ${campaign}`,
      value: 1,
      event_id: newEventId(),
      non_interaction: true,
      page: context.page,
      utm_source: context.utm_source,
      utm_medium: context.utm_medium,
      utm_campaign: context.utm_campaign,
    });
    // LinkedIn conversion, matching ContactForm — otherwise popup signups go
    // missing from paid-media reporting.
    const lintrk = (window as unknown as { lintrk?: (a: string, b: unknown) => void }).lintrk;
    if (typeof lintrk === 'function') lintrk('track', { conversion_id: 23651412 });

    // Fire-and-forget: the route runs a spam audit plus ~20-30s enrichment, and
    // keepalive lets it survive the visitor navigating away.
    fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        ...values,
        // The audit treats `message` as its strongest signal, and reads it as
        // *what the visitor wrote*. A third-person description of the form
        // ("Webinar signup — <name>") therefore reads as promotional copy: a
        // live submission came back "solicitation: Promoting a webinar to sell
        // AI services to OnePac". Phrasing it in the visitor's own voice — which
        // is what clicking "Save my seat" means — classifies as
        // genuine_prospect, verified against the live auditor for company,
        // freemail and CJK-name submissions alike.
        message:
          `[Event registration — website signup popup] ` +
          `Please save me a seat at your webinar: "${campaign}". I would like to attend.`,
        lead_label: campaign,
        ...context,
      }),
    }).catch((err) => logger.error('[popup] signup submit failed', err));

    setStep('done');
  };

  const ctaClass =
    'ph-convert flex-1 rounded-lg bg-[linear-gradient(160deg,#22ACA6_0%,#1B9A95_45%,#14807C_100%)] px-5 py-3 text-center text-[14px] font-semibold leading-tight text-white transition hover:brightness-[.94]';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <style>{SPLIT_FLAP_CSS}</style>

        <DialogPrimitive.Overlay className="duration-[250ms] fixed inset-0 z-[300] bg-[rgba(11,42,41,0.55)] backdrop-blur-[3px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Scroll container. The modal used to be centred with translate + max-h,
            which squashed the two flex columns on a short viewport and clipped
            their content with nothing to scroll to. Letting the modal keep its
            natural height inside a scrollable, centring wrapper means a viewport
            too short to hold it scrolls instead of cropping it. */}
        <div className="fixed inset-0 z-[300] overflow-y-auto overscroll-contain">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <DialogPrimitive.Content
              aria-describedby={undefined}
              onEscapeKeyDown={() => (dismissMethodRef.current = 'escape')}
              onPointerDownOutside={() => (dismissMethodRef.current = 'overlay')}
              className="duration-[250ms] relative flex w-full max-w-[420px] flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_24px_70px_-18px_rgba(11,42,41,0.55)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-3 data-[state=open]:slide-in-from-bottom-3 motion-reduce:animate-none sm:min-h-[430px] sm:max-w-[700px] sm:flex-row"
            >
              {/* ── Content column ─────────────────────────────────────────── */}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="relative h-[110px] shrink-0 sm:h-[172px]">
                  {isFilled.image(image) && (
                    <PrismicNextImage
                      field={image}
                      fill
                      sizes="(min-width: 640px) 470px, 100vw"
                      className="object-cover object-[center_32%]"
                    />
                  )}
                  {/* White fade-up so the eyebrow/headline can overlap the strip. */}
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[linear-gradient(to_top,#fff_26%,rgba(255,255,255,0)_62%)]"
                  />
                </div>

                {/* All three panels share one grid cell, so the modal's height is
                    the tallest of them and never jumps when the step changes — and
                    the inactive ones can simply cross-fade out. `inert` keeps their
                    controls out of the tab order and the a11y tree. */}
                <div className="relative -mt-[34px] grid px-6 pb-6 sm:-mt-[44px] sm:px-8 sm:pb-9">
                  {/* One Title for the whole dialog: a per-panel Title would mean
                      duplicate ids behind aria-labelledby. */}
                  <DialogPrimitive.Title className="sr-only">
                    {titleText || eyebrow || eyebrowMobile || 'Announcement'}
                  </DialogPrimitive.Title>

                  {/* ── Panel: the offer ──────────────────────────────────── */}
                  <div
                    className={panelClass(step === 'offer')}
                    inert={step !== 'offer' ? true : undefined}
                  >
                    {(eyebrow || eyebrowMobile) && (
                      <p className={EYEBROW_CLASS}>
                        <span className="sm:hidden">{eyebrowMobile || eyebrow}</span>
                        <span className="hidden sm:inline">{eyebrow || eyebrowMobile}</span>
                      </p>
                    )}

                    {isFilled.richText(title) && (
                      <h2 className={HEADING_CLASS}>
                        <PrismicRichText field={title} components={TITLE_COMPONENTS} />
                      </h2>
                    )}

                    {isFilled.richText(bullets) && (
                      <PrismicRichText field={bullets} components={BULLET_COMPONENTS} />
                    )}

                    {hasCountdown && <Countdown targetMs={targetMs} />}

                    {hasCta && (
                      <div className="mt-5 flex items-center gap-4">
                        <button type="button" onClick={handleCtaClick} className={ctaClass}>
                          {ctaLabel}
                          <LuCircleChevronRight
                            aria-hidden
                            className="ml-2 inline-block align-middle text-lg"
                          />
                        </button>
                        <a
                          href={ctaHref as string}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleMoreInfoClick}
                          className={SECONDARY_LINK_CLASS}
                        >
                          {dismissLabel || 'More Info'}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* ── Panel: the signup form ────────────────────────────── */}
                  <div
                    ref={formPanelRef}
                    className={panelClass(step === 'form')}
                    inert={step !== 'form' ? true : undefined}
                  >
                    <p className={EYEBROW_CLASS}>{formEyebrow || 'Almost there'}</p>
                    <h2 className={HEADING_CLASS}>{formTitle || 'Save your seat'}</h2>

                    <form onSubmit={(e) => handleSubmit(onSubmit)(e)} noValidate>
                      {/* Generous gap under the heading, and between the two fields —
                          a cramped form in a popup reads as an afterthought. */}
                      <div className="mt-7 space-y-6">
                        <AnimatedInput
                          label={formNameLabel || 'Name'}
                          autoComplete="name"
                          error={errors.name?.message}
                          {...register('name')}
                        />
                        <AnimatedInput
                          label={formEmailLabel || 'Business Email'}
                          type="email"
                          autoComplete="email"
                          error={errors.email?.message}
                          {...register('email')}
                        />
                      </div>

                      {/* Honeypot — hidden from people, irresistible to bots. */}
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden
                        className="absolute left-[-9999px] h-0 w-0 opacity-0"
                        {...register('hp')}
                      />

                      <div className="mt-6 flex items-center gap-4">
                        <button type="submit" disabled={isSubmitting} className={ctaClass}>
                          {formSubmitLabel || 'Submit Registration'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setStep('offer')}
                          className={SECONDARY_LINK_CLASS}
                        >
                          Back
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* ── Panel: confirmation ───────────────────────────────── */}
                  <div
                    className={`${panelClass(step === 'done')} flex flex-col justify-center`}
                    inert={step !== 'done' ? true : undefined}
                  >
                    <p className={EYEBROW_CLASS}>{successEyebrow || "You're In"}</p>
                    <p className="mt-3 text-[17px] font-medium leading-relaxed text-[#0E3B39]">
                      {formSuccess || "We'll email your joining link shortly."}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Dark rail: left column on desktop, footer on mobile ─────── */}
              <aside className="flex flex-col bg-[linear-gradient(180deg,#171717_0%,#404040_100%)] px-6 py-5 sm:order-first sm:w-[230px] sm:shrink-0 sm:bg-[linear-gradient(90deg,#404040_0%,#171717_100%)] sm:px-7 sm:py-7">
                <Image
                  src={PSGLOBAL_LOGO}
                  alt="PS Global Consulting"
                  width={90}
                  height={36}
                  className="hidden h-auto w-[90px] sm:block"
                />

                <span aria-hidden className="my-5 hidden h-0.5 bg-white/10 sm:block" />

                {dateLine && (
                  <p className="text-[18px] font-semibold text-white sm:hidden">{dateLine}</p>
                )}

                <div className="hidden sm:block">
                  {dateWeekday && (
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A3A3A3]">
                      {dateWeekday}
                    </p>
                  )}
                  {dateDisplay && (
                    <p className="mt-1.5 text-[44px] font-bold leading-[1] tracking-[-0.02em] text-white">
                      {dateDisplay}
                    </p>
                  )}
                  {dateYear && <p className="text-[18px] font-semibold text-white">{dateYear}</p>}
                </div>

                {/* Desktop: pushes the time block to the foot of the rail. Collapsed on
                mobile, where the rail is a footer and the copy follows the date line. */}
                <div aria-hidden className="hidden flex-1 sm:block" />

                {isFilled.richText(eventTimes) && (
                  <div className="mt-3 space-y-0.5 sm:mt-0">
                    <PrismicRichText field={eventTimes} components={TIME_COMPONENTS} />
                  </div>
                )}
                {eventNote && (
                  <p className="mt-[10px] text-[13px] leading-[1.45] text-[#A3A3A3]">{eventNote}</p>
                )}
              </aside>

              <DialogPrimitive.Close
                aria-label="Close"
                onClick={() => (dismissMethodRef.current = 'close_button')}
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#0E3B39] shadow-[0_2px_10px_-2px_rgba(11,42,41,0.35)] backdrop-blur-sm transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#1B9A95] focus:ring-offset-1"
              >
                <X className="h-[18px] w-[18px]" strokeWidth={2.25} />
              </DialogPrimitive.Close>
            </DialogPrimitive.Content>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
